// React & Hooks
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useState } from "react";

// Helpers
import { useTranslate } from "helpers";

// API
import { deleteFleetVehicleExpenseAPI, getFleetVehicleExpensesAPI } from "services/api/fleet/vehicles/expenses/api";
import { FleetVehicleExpenseRow } from "services/api/fleet/vehicles/expenses/types";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";

// Search & Pagination
import { Pagination } from "services/search";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// UI Components
import { NoResults } from "ui/components/NoResults";
import { Dialog } from "ui/components/Dialog";

// Local Components & Types
import { VehicleExpenseSearchCardProps, VehicleExpenseSearchRef } from "./types";
import VehicleExpenseSearchResults from "../VehicleExpenseSearchResults/VehicleExpenseSearchResults";
import VehicleExpenseEditCard from "../../../VehicleExpenseEditCard/VehicleExpenseEditCard";

const VehicleExpenseSearchCard = forwardRef<
  VehicleExpenseSearchRef,
  VehicleExpenseSearchCardProps
>((props, ref) => {

  const { fleetVehicleDetail } = props;

  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetVehicleExpenseRow>
  >(initialDataWithPagination);

  const [expenseId, setExpenseId] = useState<string>("");
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
  }, [fleetVehicleDetail.id]);

  const fetchSearchResult = async (
    pagination: Pagination,
    vehicleId: string
  ) => {
    setIsLoading(true);
    try {
      const results = await getFleetVehicleExpensesAPI(pagination, { vehicleId });

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    setExpenseId(id);
    setShowAddNewModal(true);
  }, []);

  const openAddNew = () => {
    setExpenseId("");
    setShowAddNewModal(true);
  };

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
  };

  const handleEditSuccess = useCallback(() => {
    setShowAddNewModal(false);

    if (fleetVehicleDetail.id)
      fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
  }, [fetchSearchResult]);


  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setExpenseId(id);
    setOpenDialog(true);
  }

  const handleExpenseDeleteDialogConfirm = useCallback(async () => {
    if (!expenseId) return;

    try {
      const deleted = await deleteFleetVehicleExpenseAPI(
        expenseId,
        t
      );

      if (deleted && fleetVehicleDetail.id) {
        await fetchSearchResult(searchResults.pagination, fleetVehicleDetail.id);
      }

      setOpenDialog(false);
      setExpenseId("");
    } catch (err) {
      console.error("Greška prilikom brisanja troška:", err);
    }
  }, [expenseId, t, fleetVehicleDetail.id, searchResults.pagination]);

  const handleExpenseDeleteDialogClose = useCallback(() => {
    setExpenseId("");
    setOpenDialog(false);
  }, []);

  useImperativeHandle(ref, () => ({
    openAddNew,
  }));

  if (!fleetVehicleDetail.id) return <></>;

  return (
    <>
      <div className="space-y-3">
        <div className="m-[-1px]">
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults
              message={t("admin.fleet.vehicle.expense.search.noresults")}
            />
          ) : (
            <VehicleExpenseSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
              onDeleteClick={handleDeleteClick}
            />
          )}
        </div>
      </div>

      <VehicleExpenseEditCard
        show={showAddNewModal}
        vehicleId={fleetVehicleDetail.id}
        expenseId={expenseId}
        onSuccess={handleEditSuccess}
        onClose={() => setShowAddNewModal(false)}
      />
      <Dialog
        open={openDialog}
        title={t(
          "admin.fleet.vehicle.expense.dialog.delete.title"
        )}
        message={t(
          "admin.fleet.vehicle.expense.dialog.delete.message"
        )}
        confirmText={t(
          "admin.fleet.vehicle.expense.dialog.delete.confirmtext"
        )}
        cancelText={t(
          "admin.fleet.vehicle.expense.dialog.delete.canceltext"
        )}
        onConfirm={handleExpenseDeleteDialogConfirm}
        onCancel={handleExpenseDeleteDialogClose}
      />
    </>
  );
});

export default memo(VehicleExpenseSearchCard);
